/**
 * Card primitive — a simple bordered container with optional title.
 *
 * Spec: PORTAL_MIGRATION_FROM_PSM_SSOT.md §4.1.2 (Phase 1 foundation).
 * Inline-style adaptation of PSM `components/ui/card.tsx`. Phase 2-4
 * pages should compose `Card` + `CardHeader` + `CardBody` + `CardFooter`
 * rather than nesting raw <div>s.
 */
import type { ReactNode } from 'react';

interface CardProps {
  children: ReactNode;
  /** Optional aria role; defaults to `region` when `aria-label` is set. */
  role?: string;
  'aria-label'?: string;
  style?: React.CSSProperties;
  className?: string;
}

export function Card({ children, role, style, className, ...aria }: CardProps): JSX.Element {
  const resolvedRole = role ?? (aria['aria-label'] ? 'region' : undefined);
  return (
    <section
      role={resolvedRole}
      aria-label={aria['aria-label']}
      className={className}
      style={{
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: '0.5rem',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
        overflow: 'hidden',
        ...style,
      }}
    >
      {children}
    </section>
  );
}

export function CardHeader({
  children,
  style,
}: {
  children: ReactNode;
  style?: React.CSSProperties;
}): JSX.Element {
  return (
    <header
      style={{
        padding: '1rem 1.25rem',
        borderBottom: '1px solid #e5e7eb',
        background: '#fafafa',
        ...style,
      }}
    >
      {children}
    </header>
  );
}

export function CardTitle({
  children,
  level = 2,
}: {
  children: ReactNode;
  level?: 1 | 2 | 3 | 4;
}): JSX.Element {
  const baseStyle: React.CSSProperties = {
    margin: 0,
    fontSize: '1rem',
    fontWeight: 600,
    color: '#111827',
  };
  switch (level) {
    case 1:
      return <h1 style={baseStyle}>{children}</h1>;
    case 3:
      return <h3 style={baseStyle}>{children}</h3>;
    case 4:
      return <h4 style={baseStyle}>{children}</h4>;
    case 2:
    default:
      return <h2 style={baseStyle}>{children}</h2>;
  }
}

export function CardBody({
  children,
  style,
}: {
  children: ReactNode;
  style?: React.CSSProperties;
}): JSX.Element {
  return <div style={{ padding: '1rem 1.25rem', ...style }}>{children}</div>;
}

export function CardFooter({
  children,
  style,
}: {
  children: ReactNode;
  style?: React.CSSProperties;
}): JSX.Element {
  return (
    <footer
      style={{
        padding: '0.75rem 1.25rem',
        borderTop: '1px solid #e5e7eb',
        background: '#fafafa',
        ...style,
      }}
    >
      {children}
    </footer>
  );
}
